import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { api } from '../api/nocode';
import { TABLES } from '../api/tables';
import { useAuth } from './AuthContext';

const AcademyContext = createContext(null);

function rowsFrom(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function enabled(record) {
  return record?.is_active === 1 ||
    record?.is_active === true ||
    record?.is_active === '1' ||
    record?.status === 'active';
}

function enrollmentIsActive(enrollment) {
  // Free pass access is treated the same as paid pass access for all modules and function
  if (!['free', 'paid', 'allaccess', 'evergreen', 'evergreen-aap'].includes(enrollment?.pass_type)) {
    return false;
  }

  const status = String(enrollment?.status ?? 'active').toLowerCase();
  return ['active', 'completed'].includes(status);
}

// passes_gwaacademy is the reliable source of "which student is connected to
// which event run" — it carries no per-column uniqueness, unlike the legacy
// enrollments_gwaacademy table (whose single-column unique constraints on
// user_id/run_id silently reject every enrollment row after the very first
// one for a given student or a given run). Any row here for a run counts as
// that student's access to that run.
function passIsActive(pass) {
  return ['paid', 'allaccess', 'comped'].includes(
    String(pass?.pass_type ?? '').toLowerCase()
  );
}

function passRank(record) {
  const type = String(record?.pass_type ?? '').toLowerCase();
  if (['allaccess', 'evergreen-aap'].includes(type)) return 2;
  if (['free', 'paid', 'evergreen', 'comped'].includes(type)) return 1;
  return 0;
}

function strongestRecord(records) {
  return [...records].sort(
    (a, b) => passRank(b) - passRank(a)
  )[0] ?? null;
}

export function getRunSlug(run) {
  return run?.slug || (run?.id ? `run-${run.id}` : '');
}

export function AcademyProvider({ children }) {
  const { user } = useAuth();
  const safeUser = Array.isArray(user) ? user[0] : user;
  const [departments, setDepartments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [passes, setPasses] = useState([]);
  const [currentRun, setCurrentRun] = useState(null);
  const [loadingAcademy, setLoadingAcademy] = useState(true);

  const refreshAcademy = useCallback(async () => {
    setLoadingAcademy(true);

    try {
      const [departmentResponse, sessionResponse, runResponse] =
        await Promise.all([
          api.get(TABLES.departments).catch(() => []),
          api.get(TABLES.sessions).catch(() => []),
          api.get(TABLES.runs).catch(() => [])
        ]);

      const nextDepartments = rowsFrom(departmentResponse);
      const nextSessions = rowsFrom(sessionResponse);
      const nextRuns = rowsFrom(runResponse);

      setDepartments(nextDepartments);
      setSessions(nextSessions);
      setRuns(nextRuns);

      if (safeUser?.id && safeUser?.role !== 'admin') {
        const [enrollmentResponse, passResponse] = await Promise.all([
          api.getUnscoped(
            TABLES.enrollments,
            {
              owner_id: safeUser.id,
              user_id: safeUser.id
            }
          ).catch(() => []),
          api.getUnscoped(
            TABLES.passes,
            { user_id: safeUser.id }
          ).catch(() => [])
        ]);

        setEnrollments(rowsFrom(enrollmentResponse));
        setPasses(rowsFrom(passResponse));
      } else if (safeUser?.role === 'admin') {
        const adminAccess = nextRuns.map((run) => ({
          id: `admin-${run.id}`,
          run_id: run.id,
          session_id: run.session_id,
          pass_type: 'allaccess',
          status: 'active'
        }));
        setEnrollments(adminAccess);
        setPasses(adminAccess);
      } else {
        setEnrollments([]);
        setPasses([]);
      }

      const storedId = localStorage.getItem('gwa_current_run_id');
      const storedRun = nextRuns.find(
        (run) => String(run?.id) === String(storedId)
      );
      const fallbackRun = nextRuns.find(enabled) ?? null;
      const selectedRun = storedRun ?? fallbackRun;

      if (selectedRun) {
        localStorage.setItem('gwa_current_run_id', String(selectedRun.id));

        if (!localStorage.getItem('gwa_legacy_run_id')) {
          localStorage.setItem('gwa_legacy_run_id', String(selectedRun.id));
        }
      }

      setCurrentRun(selectedRun);

      const storedSessionId = localStorage.getItem(
        'gwa_current_session_id'
      );
      const storedSession = nextSessions.find(
        (session) => String(session?.id) === String(storedSessionId)
      );

      if (
        storedSession &&
        String(selectedRun?.session_id) !== String(storedSession.id)
      ) {
        localStorage.removeItem('gwa_current_run_id');
        setCurrentRun({ session_id: storedSession.id });
      }
    } catch (error) {
      console.error('Unable to load learning centers and sessions', error);
      setDepartments([]);
      setSessions([]);
      setRuns([]);
      setEnrollments([]);
      setPasses([]);
      setCurrentRun(null);
    } finally {
      setLoadingAcademy(false);
    }
  }, [safeUser?.id, safeUser?.role]);

  useEffect(() => {
    refreshAcademy();
  }, [refreshAcademy]);

  useEffect(() => {
    if (currentRun?.id) {
      localStorage.setItem(
        'gwa_class_in_session',
        enabled(currentRun) ? 'true' : 'false'
      );
    } else {
      localStorage.removeItem('gwa_class_in_session');
    }
  }, [currentRun]);

  const selectRun = useCallback((run) => {
    if (!run?.id) return;

    localStorage.setItem('gwa_current_run_id', String(run.id));
    localStorage.setItem(
      'gwa_class_in_session',
      enabled(run) ? 'true' : 'false'
    );
    setCurrentRun(run);
    return run;
  }, []);

  const selectSession = useCallback((session) => {
    if (!session?.id) return;

    localStorage.setItem(
      'gwa_current_session_id',
      String(session.id)
    );

    if (session?.department_id) {
      localStorage.setItem(
        'gwa_current_department_id',
        String(session.department_id)
      );
    }

    const sessionRun = runs.find(
      (run) => String(run?.session_id) === String(session.id)
    );

    if (sessionRun) {
      selectRun(sessionRun);
    } else {
      localStorage.removeItem('gwa_current_run_id');
      localStorage.removeItem('gwa_class_in_session');
      setCurrentRun({ session_id: session.id });
    }
  }, [runs, selectRun]);

  const passForRun = useCallback((run) => {
    if (!run?.id) return null;

    return strongestRecord(
      passes.filter(
        (pass) =>
          String(pass?.run_id) === String(run.id) &&
          passIsActive(pass)
      )
    );
  }, [passes]);

  const enrollmentForRun = useCallback((run) => {
    if (!run?.id) return null;

    return strongestRecord(
      enrollments.filter(
        (enrollment) =>
          String(enrollment?.run_id) === String(run.id) &&
          enrollmentIsActive(enrollment)
      )
    );
  }, [enrollments]);

  const userCanAccessRun = useCallback((run) => {
    if (!run) return false;
    if (safeUser?.role === 'admin') return true;
    return Boolean(passForRun(run)) || Boolean(enrollmentForRun(run));
  }, [passForRun, enrollmentForRun, safeUser?.role]);

  const accessibleRuns = useMemo(() => {
    if (safeUser?.role === 'admin') return runs;
    return runs.filter((run) => userCanAccessRun(run));
  }, [runs, safeUser?.role, userCanAccessRun]);

  const currentEnrollment = useMemo(() => {
    const pass = passForRun(currentRun);
    const enrollment = enrollmentForRun(currentRun);
    const access = strongestRecord(
      [pass, enrollment].filter(Boolean)
    );

    if (access) {
      const canonicalPassType = safeUser?.role === 'admin'
        ? 'allaccess'
        : String(safeUser?.pass_type ?? 'free').toLowerCase();

      return enrollment
        ? { ...enrollment, pass_type: canonicalPassType }
        : {
          run_id: currentRun?.id,
          session_id: currentRun?.session_id,
          pass_type: canonicalPassType,
          status: 'active'
        };
    }

    return enrollment;
  }, [
    currentRun,
    enrollmentForRun,
    passForRun,
    safeUser?.pass_type,
    safeUser?.role
  ]);

  const currentSession = useMemo(() => {
    return sessions.find(
      (session) => String(session?.id) === String(currentRun?.session_id)
    ) ?? null;
  }, [currentRun, sessions]);

  const currentDepartment = useMemo(() => {
    return departments.find(
      (department) =>
        String(department?.id) === String(currentSession?.department_id)
    ) ?? null;
  }, [currentSession, departments]);

  useEffect(() => {
    if (currentSession?.id) {
      localStorage.setItem(
        'gwa_current_session_id',
        String(currentSession.id)
      );
    } else {
      localStorage.removeItem('gwa_current_session_id');
    }

    if (currentDepartment?.id) {
      localStorage.setItem(
        'gwa_current_department_id',
        String(currentDepartment.id)
      );
    } else {
      localStorage.removeItem('gwa_current_department_id');
    }
  }, [currentDepartment, currentSession]);

  useEffect(() => {
    if (
      safeUser?.role !== 'admin' &&
      safeUser?.id &&
      currentRun?.id &&
      currentEnrollment?.pass_type
    ) {
      localStorage.setItem(
        'gwa_run_access_user_id',
        String(safeUser.id)
      );
      localStorage.setItem(
        'gwa_run_access_run_id',
        String(currentRun.id)
      );
      localStorage.setItem(
        'gwa_run_pass_type',
        String(currentEnrollment.pass_type).toLowerCase()
      );
    } else {
      localStorage.removeItem('gwa_run_access_user_id');
      localStorage.removeItem('gwa_run_access_run_id');
      localStorage.removeItem('gwa_run_pass_type');
    }
  }, [
    currentEnrollment?.pass_type,
    currentRun?.id,
    safeUser?.id,
    safeUser?.role
  ]);

  const scope = useMemo(() => ({
    department_id: currentDepartment?.id
      ? String(currentDepartment.id)
      : '',
    session_id: currentSession?.id ? String(currentSession.id) : '',
    run_id: currentRun?.id ? String(currentRun.id) : ''
  }), [currentDepartment, currentRun, currentSession]);

  const value = {
    departments,
    sessions,
    runs,
    enrollments,
    passes,
    accessibleRuns,
    currentRun,
    currentSession,
    currentDepartment,
    currentEnrollment,
    scope,
    loadingAcademy,
    refreshAcademy,
    selectRun,
    selectSession,
    enrollmentForRun,
    passForRun,
    userCanAccessRun,
    getRunSlug,
    isEnabled: enabled
  };

  return (
    <AcademyContext.Provider value={value}>
      {children}
    </AcademyContext.Provider>
  );
}

export function useAcademy() {
  const context = useContext(AcademyContext);

  if (!context) {
    throw new Error('useAcademy must be used inside AcademyProvider');
  }

  return context;
}